import { useMutation, useQueryClient } from "@tanstack/react-query";
import { getCsrfCookie } from "../lib/utils";
import { REEPRISE_LIST_QUERY_KEY } from "./useGeneratedTextPaginatedList";
import { TEXT_STATISTICS_QUERY_KEY } from "./useTextStatistics";
import { invalidateQuery as invalidateStatistics } from "./useVocabularyStatistics";
import { GeneratedText } from "./types/GeneratedTexts";
import { REEPRISE_DETAILS_QUERY_KEY } from "./useGeneratedTextDetails";

export function useGeneratedTextCreateFromArticleUrl() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (arg: { url: string; language: string }) => {
      const res = await fetch(
        "/api/reeprise/generated_texts/article-url-reading-text/",
        {
          method: "POST",
          headers: {
            "content-type": "application/json",
            "X-CSRFTOKEN": getCsrfCookie(),
          },
          body: JSON.stringify(arg),
        },
      );
      if (!res.ok) {
        if (res.status === 401 || res.status === 403) {
          throw new Error("AUTH_REQUIRED");
        }
        let detail = "Could not create reading text from article URL";
        try {
          const body = await res.json();
          if (typeof body?.detail === "string" && body.detail.trim()) {
            detail = body.detail;
          } else if (typeof body?.url?.[0] === "string" && body.url[0].trim()) {
            detail = body.url[0];
          }
        } catch {
          // Keep the generic message.
        }
        throw new Error(detail);
      }
      return (await res.json()) as GeneratedText;
    },
    onSettled: (data) => {
      if (data !== undefined) {
        void queryClient.setQueryData(
          [REEPRISE_DETAILS_QUERY_KEY, `${data.id}`],
          data,
        );
      }
      void queryClient.invalidateQueries({
        queryKey: [REEPRISE_LIST_QUERY_KEY],
      });
      void queryClient.invalidateQueries({
        queryKey: [TEXT_STATISTICS_QUERY_KEY],
      });
      invalidateStatistics(queryClient);
    },
  });
}
